import { Link as LinkIcon } from "@phosphor-icons/react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { Sidebar } from "features/layout/components";

import { Dialog, OverlayDialogContent } from "common/components/ui/Dialog";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAccountSync from "../../datahooks/useAccountSync";
import useAccountActions from "../../hooks/useAccountActions";
import LoginStatusTag from "../AccountTags/LoginStatusTag";
import AccordionSection from "./AccordionSection";
import AutoAcceptConnectionsRow from "./AutoAcceptRow";
import CooldownRow from "./CooldownRow";
import DetailItem from "./DetailItem";
import Header from "./Header";
import LinkedInRow from "./LinkedInRow";
import SeatRow from "./SeatRow";

export type { DetailItemProps } from "./DetailItem";

export default function AccountSettings() {
  const isDesktop = useTwBreakpoint("lg");
  const { accountId } = useParams();
  const { accounts } = useSelectedWorkspaceContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { syncAccount, isSyncingAccount } = useAccountSync();

  const account = accounts.find(({ id }) => id === accountId);
  const { occupation, state, license, picture_url, type, public_identifier } =
    account ?? {};

  const { selectAction, components } = useAccountActions(account);

  if (!account) {
    return <Navigate to={{ pathname: ".." }} replace />;
  }

  const isLoggedIn = state === "LoggedIn";

  function goBack() {
    navigate({
      pathname: "..",
      search: location.search,
    });
  }

  const content = (
    <>
      <Header
        account={account}
        isDesktop={isDesktop}
        pictureUrl={picture_url}
        occupation={occupation}
      />

      <LinkedInRow
        isLoggedIn={isLoggedIn}
        isSyncingAccount={isSyncingAccount}
        publicIdentifier={public_identifier}
        onSync={() => syncAccount({ accountId: account.id })}
      />

      <DetailItem label="Login" icon={<LinkIcon weight="bold" />}>
        <LoginStatusTag account={account} size="md" />
      </DetailItem>

      <SeatRow license={license} isRented={type === "rental"} />
      <AutoAcceptConnectionsRow accountId={account.id} />
      <CooldownRow accountId={account.id} />

      <AccordionSection
        account={account}
        accountId={accountId}
        isLoggedIn={isLoggedIn}
        onReconnect={() => {
          selectAction("reconnect");
        }}
      />
      {components}
    </>
  );

  if (isDesktop) {
    return (
      <Sidebar
        isOpen
        onClose={() => navigate({ pathname: "..", search: location.search })}
      >
        {content}
      </Sidebar>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          goBack();
        }
      }}
    >
      <OverlayDialogContent>{content}</OverlayDialogContent>
    </Dialog>
  );
}
